import { Box, Button, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import {
    AlertCircle,
    ArrowRight,
    FileUp,
    Info,
    List as ListIcon,
    Minus,
    Package,
    Plus,
    Search,
    ShoppingCart,
    Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { standaloneFieldSx } from '../../common/fieldStyles';
import { httpClient } from '../../common/httpclient';
import Dropdown from '../../component/dropdown/Dropdown';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobilePurchaseManual from '../../component/purchase/MobilePurchaseManual';

// Nova compra manual (lançamento item a item). Reaproveita POST /purchases/confirm
// (o mesmo do Import.js): item de produto existente => action "update"; produto novo =>
// action "create". Ref visual: prototipo-compras-itens-manual.html.

const asList = (data, key) => {
    if (Array.isArray(data)) return data;
    if (key && data && Array.isArray(data[key])) return data[key];
    return [];
};

const errorText = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data.error === 'string') return data.error;
    return fallback;
};

const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// "12,5" | "12.50" | "1.234,56" -> "12,50" (formato exigido pela API) ou '' se inválido/zero.
const toBRL = (raw) => {
    let s = String(raw || '').trim().replace(/\s/g, '');
    if (!s) return '';
    if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
    else if (s.includes(',')) s = s.replace(',', '.');
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return '';
    return n.toFixed(2).replace('.', ',');
};

// Custo vindo do cadastro ("R$ 1.234,56" | "10,5" | "10.50") -> "1.234,56" normalizado; '' se zero/inválido.
const costFromProduct = (raw) => toBRL(String(raw || '').replace(/[^\d.,]/g, ''));

const toCents = (raw) => {
    const v = toBRL(raw);
    return v ? Math.round(Number(v.replace(',', '.')) * 100) : 0;
};
const centsToBRL = (c) => (c / 100).toFixed(2).replace('.', ',');
// data de hoje (local) no formato AAAA-MM-DD, padrão do vencimento
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const money = (c) => (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const pad = (n) => String(n).padStart(2, '0');
const autoReference = () => {
    const d = new Date();
    return `MAN-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

const EMPTY_NEW = { name: '', code: '', category: '', subcategory: '', qty: '1', cost: '' };

const cardSx = {
    backgroundColor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: (t) => `${t.customRadius.card}px`,
    boxShadow: (t) => t.customShadows.sm,
    p: '20px',
};

const sectionTitleSx = { fontSize: '14.5px', fontWeight: 700, lineHeight: 1.4 };
const sectionSubSx = { fontSize: 12, color: 'text.disabled', mt: '2px' };

function SectionIcon({ children }) {
    return (
        <Box sx={{
            width: 30, height: 30, borderRadius: '9px', flexShrink: 0, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: (t) => t.palette.gradient.brand,
        }}>
            {children}
        </Box>
    );
}

function Kbd({ children }) {
    return (
        <Box component="span" sx={{
            fontSize: '10.5px', fontWeight: 700, px: '6px', py: '2px', borderRadius: '6px',
            border: '1px solid', borderColor: 'divider', backgroundColor: 'background.subtle', color: 'text.secondary',
            fontFamily: 'ui-monospace, Menlo, monospace',
        }}>
            {children}
        </Box>
    );
}

function Pill({ tone = 'info', children, icon }) {
    const colors = {
        info: ['info.bg', 'info.main'],
        purple: ['secondary.bg', 'secondary.main'],
        grey: ['background.subtle', 'text.disabled'],
    }[tone];
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 11, fontWeight: 700,
            px: '10px', py: '4px', borderRadius: '20px', whiteSpace: 'nowrap', backgroundColor: colors[0], color: colors[1],
        }}>
            {icon}{children}
        </Box>
    );
}

function Mark({ text, query }) {
    const i = query ? norm(text).indexOf(norm(query)) : -1;
    if (i < 0) return text;
    return (
        <>
            {text.slice(0, i)}
            <Box component="mark" sx={{ backgroundColor: 'warning.bg', color: 'inherit', borderRadius: '3px', p: '0 1px' }}>
                {text.slice(i, i + query.length)}
            </Box>
            {text.slice(i + query.length)}
        </>
    );
}

const errFieldSx = (hasError) => (hasError ? {
    '& .MuiOutlinedInput-root': { backgroundColor: 'error.bg' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
    '& .MuiInputLabel-root, & .MuiInputLabel-root.MuiInputLabel-shrink': { color: 'error.main' },
} : {});

function FieldError({ children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'error.main', fontSize: 11.5, fontWeight: 600, mt: '5px' }}>
            <AlertCircle size={13} strokeWidth={2} />{children}
        </Box>
    );
}

const qtyBtnSx = {
    width: 28, height: 32, borderRadius: 0, color: 'text.secondary', fontSize: 15,
    '&:hover': { backgroundColor: 'background.paper', color: 'primary.main' },
};

export default function Manual() {
    const navigate = useNavigate();
    const isMobileShell = useIsMobileShell();
    const searchRef = useRef(null);
    const qtyRef = useRef(null);
    const newCodeRef = useRef(null);
    const referenceRef = useRef(''); // número automático gerado no 1º envio (estável em reenvios parciais)

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [subcategoryNames, setSubcategoryNames] = useState({}); // subcategory_id -> nome (GET /stock/all só traz IDs)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [supplierId, setSupplierId] = useState('');
    const [reference, setReference] = useState('');
    const [dueDate, setDueDate] = useState(todayISO());
    const [items, setItems] = useState([]); // { key, kind: 'existing'|'new', p, qty, error?, flash? }
    const [query, setQuery] = useState('');
    const [listOpen, setListOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const [hl, setHl] = useState(-1);
    const [mode, setMode] = useState('idle'); // idle | found | new
    const [selected, setSelected] = useState(null);
    const [qty, setQty] = useState('1');
    const [qtyError, setQtyError] = useState(false);
    const [cost, setCost] = useState('');
    const [costError, setCostError] = useState(false);
    const costRef = useRef(null);
    const [form, setForm] = useState(EMPTY_NEW);
    const [showErrors, setShowErrors] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const toast = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    useEffect(() => {
        httpClient.get('/stock/all').then((r) => setProducts(asList(r.data, 'items').filter((p) => !p.is_generic))).catch(() => setProducts([]));
        httpClient.get('/stock/categories').then((r) => {
            const list = asList(r.data, 'categories');
            setCategories(list);
            Promise.all(list.map((c) => httpClient.get('/stock/subcategories', { params: { categoryId: c.id } })
                .then((res) => asList(res.data, 'subcategories')).catch(() => [])))
                .then((groups) => setSubcategoryNames(Object.fromEntries(groups.flat().map((sc) => [sc.id, sc.name]))));
        }).catch(() => setCategories([]));
        httpClient.get('/stock/suppliers').then((r) => setSuppliers(asList(r.data, 'suppliers'))).catch(() => setSuppliers([]));
    }, []);

    useEffect(() => {
        if (!form.category) {
            setSubcategories([]);
            return;
        }
        httpClient
            .get('/stock/subcategories', { params: { categoryId: form.category } })
            .then((r) => setSubcategories(asList(r.data, 'subcategories')))
            .catch(() => setSubcategories([]));
    }, [form.category]);

    // ---------- busca ----------
    const q = query.trim();
    const matches = useMemo(() => {
        if (!q) return [];
        const nq = norm(q);
        return products
            .filter((p) => norm(p.name).includes(nq) || norm(p.code).includes(nq))
            .slice(0, 6);
    }, [products, q]);
    const exact = useMemo(() => products.some((p) => norm(p.name) === norm(q)), [products, q]);
    const optionCount = matches.length + (exact ? 0 : 1);

    const catName = (p) => p.category_name || categories.find((c) => c.id === p.category_id)?.name || '';
    const subName = (p) => p.subcategory_name || subcategoryNames[p.subcategory_id] || '';

    // estoque do produto considerando o que já está na lista desta compra
    const stockOf = (p) => (p.quantity || 0) + items.filter((i) => i.kind === 'existing' && i.p.id === p.id).reduce((a, i) => a + i.qty, 0);

    const resetEntry = () => {
        setMode('idle');
        setSelected(null);
        setQuery('');
        setQty('1');
        setQtyError(false);
        setCost('');
        setCostError(false);
        setForm(EMPTY_NEW);
        setShowErrors(false);
        setListOpen(false);
        setHl(-1);
        searchRef.current?.focus();
    };

    const selectExisting = (p) => {
        setSelected(p);
        setMode('found');
        setShowErrors(false);
        setQuery(p.name);
        setQty('1');
        setCost(costFromProduct(p.cost));
        setCostError(false);
        setListOpen(false);
        setHl(-1);
    };

    const startNew = (name) => {
        setMode('new');
        setSelected(null);
        setShowErrors(false);
        setForm({ ...EMPTY_NEW, name });
        setListOpen(false);
        setHl(-1);
    };

    const chooseOption = (i) => {
        if (i < matches.length) selectExisting(matches[i]);
        else startNew(q);
    };

    const onQueryChange = (e) => {
        setQuery(e.target.value);
        setHl(-1);
        setListOpen(true);
        if (mode !== 'idle') {
            setMode('idle');
            setSelected(null);
        }
    };

    const onQueryKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setListOpen(true);
            setHl((h) => Math.min(optionCount - 1, h + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHl((h) => Math.max(0, h - 1));
        } else if (e.key === 'Escape') {
            setListOpen(false);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (!q) return;
            chooseOption(hl < 0 ? 0 : hl);
        }
    };

    // foco no campo certo assim que o painel monta (quantidade / código do novo produto)
    useEffect(() => {
        if (mode === 'found') { qtyRef.current?.focus(); qtyRef.current?.select(); }
        else if (mode === 'new') newCodeRef.current?.focus();
    }, [mode, selected]);

    // ---------- adicionar ----------
    const flashKey = useRef(null);

    const addFound = () => {
        const n = parseInt(qty, 10) || 0;
        if (n < 1) {
            setQtyError(true);
            qtyRef.current?.focus();
            return;
        }
        const unitCost = toBRL(cost);
        if (!unitCost) {
            setCostError(true);
            costRef.current?.focus();
            return;
        }
        const ex = items.find((i) => i.kind === 'existing' && i.p.id === selected.id);
        if (ex) {
            flashKey.current = ex.key;
            const changed = toCents(ex.cost) !== toCents(unitCost);
            setItems((cur) => cur.map((i) => (i.key === ex.key ? { ...i, qty: i.qty + n, cost: unitCost, error: '' } : i)));
            toast(
                changed
                    ? `Quantidade somada: ${selected.name} agora com ${ex.qty + n} un. Custo unitário atualizado para R$ ${unitCost} (vale para todas as unidades).`
                    : `Quantidade somada: ${selected.name} agora com ${ex.qty + n} un`,
                changed ? 'warning' : 'success',
            );
        } else {
            const key = `e-${selected.id}`;
            flashKey.current = key;
            setItems((cur) => [{ key, kind: 'existing', p: selected, qty: n, cost: unitCost }, ...cur]);
            toast(`Adicionado: ${selected.name} (+${n})`);
        }
        resetEntry();
    };

    const newErrors = useMemo(() => {
        const code = form.code.trim();
        const dup = code && (
            products.find((p) => norm(p.code) === norm(code))
            || items.find((i) => i.kind === 'new' && norm(i.p.code) === norm(code))?.p
        );
        return {
            name: !form.name.trim(),
            code: !code || Boolean(dup),
            codeMsg: dup ? `Código já usado por “${dup.name}”.` : 'Informe o código.',
            category: !form.category,
            subcategory: !form.subcategory,
            qty: (parseInt(form.qty, 10) || 0) < 1,
            cost: !toBRL(form.cost),
        };
    }, [form, products, items]);

    const newValid = !['name', 'code', 'category', 'subcategory', 'qty', 'cost'].some((k) => newErrors[k]);

    const addNew = () => {
        setShowErrors(true);
        if (!newValid) return;
        const cat = categories.find((c) => c.id === form.category);
        const sub = subcategories.find((s) => s.id === form.subcategory);
        const p = {
            name: form.name.trim(),
            code: form.code.trim(),
            categoryId: form.category,
            subcategoryId: form.subcategory,
            category: cat?.name || '',
            subcategory: sub?.name || '',
            markup: cat?.profit_margin ? Number(String(cat.profit_margin).replace(',', '.')) || 0 : 0,
            cost: toBRL(form.cost),
            quantity: 0,
        };
        const key = `n-${Date.now()}`;
        flashKey.current = key;
        setItems((cur) => [{ key, kind: 'new', p, qty: parseInt(form.qty, 10), cost: p.cost }, ...cur]);
        toast(`Novo produto adicionado: ${p.name}`);
        resetEntry();
    };

    const onNewKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addNew();
        }
    };

    const generateCode = () => {
        const taken = new Set([...products.map((p) => norm(p.code)), ...items.map((i) => norm(i.p.code))]);
        let c;
        do { c = String(9200 + Math.floor(Math.random() * 700)); } while (taken.has(c));
        setForm((f) => ({ ...f, code: c }));
    };

    // ---------- itens ----------
    const setItemQty = (key, value) => setItems((cur) => cur.map((i) => (i.key === key ? { ...i, qty: Math.max(1, value), error: '' } : i)));
    const setItemCost = (key, value) => setItems((cur) => cur.map((i) => (i.key === key ? { ...i, cost: value, error: '' } : i)));
    const removeItem = (key) => {
        setItems((cur) => cur.filter((i) => i.key !== key));
        toast('Item removido', 'warning');
    };
    const clearItems = () => {
        setItems([]);
        toast('Lista limpa', 'warning');
    };

    const totalUnits = items.reduce((a, i) => a + i.qty, 0);
    const newCount = items.filter((i) => i.kind === 'new').length;
    const totalCents = items.reduce((a, i) => a + toCents(i.cost) * i.qty, 0);

    // ---------- finalizar ----------
    const finish = async () => {
        if (!items.length || submitting) return;
        if (items.some((i) => !toBRL(i.cost))) {
            setItems((cur) => cur.map((i) => (toBRL(i.cost) ? i : { ...i, error: 'Informe o custo unitário (maior que 0).' })));
            toast('Informe o custo unitário de todos os itens.', 'error');
            return;
        }
        if (!referenceRef.current) referenceRef.current = autoReference();
        const number = reference.trim() || referenceRef.current;
        const ordered = [...items].reverse();
        const payloadItems = ordered.map((it, index) => {
            if (it.kind === 'existing') {
                return { index, action: 'update', product_id: it.p.id, quantity: it.qty, unit_cost: toBRL(it.cost) };
            }
            return {
                index,
                action: 'create',
                quantity: it.qty,
                unit_cost: toBRL(it.cost),
                code: it.p.code,
                name: it.p.name,
                description: '',
                category_id: it.p.categoryId,
                subcategory_id: it.p.subcategoryId,
                markup_percent: it.p.markup,
                min_quantity: 0,
            };
        });

        setSubmitting(true);
        try {
            const response = await httpClient.post('/purchases/confirm', {
                update_cost: false,
                invoices: [{
                    file_name: 'compra-manual',
                    access_key: '',
                    number,
                    series: '',
                    issue_date: new Date().toISOString().slice(0, 10),
                    total: centsToBRL(ordered.reduce((a, it) => a + toCents(it.cost) * it.qty, 0)),
                    due_date: dueDate || todayISO(),
                    supplier_id: supplierId,
                    create_supplier: false,
                    supplier: {},
                    items: payloadItems,
                }],
            });
            if (response.status >= 400) {
                toast(errorText({ response }, 'Erro ao lançar a compra.'), 'error');
                return;
            }
            const result = asList(response.data, 'invoices')[0];
            if (!result) {
                toast('Erro ao lançar a compra.', 'error');
                return;
            }
            const byIndex = new Map(asList(result.items).map((r) => [r.index, r]));
            const failed = ordered.filter((_, index) => byIndex.get(index)?.status !== 'ok');
            const launched = ordered.length - failed.length;

            if (!failed.length) {
                toast(`Compra finalizada: ${launched} ${launched === 1 ? 'item' : 'itens'}, estoque atualizado.`);
                referenceRef.current = '';
                setItems([]);
                setReference('');
                setDueDate(todayISO());
                resetEntry();
                // recarrega o catálogo para refletir estoque novo e produtos criados
                httpClient.get('/stock/all').then((r) => setProducts(asList(r.data, 'items').filter((p) => !p.is_generic))).catch(() => {});
                return;
            }

            setItems(failed.reverse().map((it) => {
                const idx = ordered.indexOf(it);
                return { ...it, error: byIndex.get(idx)?.message || result.message || 'Não lançado' };
            }));
            toast(
                launched
                    ? `${launched} de ${ordered.length} item(ns) lançados. Corrija os itens com erro e finalize novamente.`
                    : (result.message || 'Nenhum item foi lançado.'),
                launched ? 'warning' : 'error',
            );
            httpClient.get('/stock/all').then((r) => setProducts(asList(r.data, 'items').filter((p) => !p.is_generic))).catch(() => {});
        } catch (error) {
            toast(errorText(error, 'Erro ao lançar a compra.'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const flashing = flashKey.current;
    useEffect(() => {
        if (!flashKey.current) return undefined;
        const t = setTimeout(() => { flashKey.current = null; }, 1400);
        return () => clearTimeout(t);
    }, [items]);

    const reqLabel = (text) => (<>{text} <Box component="span" sx={{ color: 'error.main' }}>*</Box></>);
    const found = mode === 'found' && selected;
    const curStock = found ? stockOf(selected) : 0;
    const qtyNum = Math.max(0, parseInt(qty, 10) || 0);

    // Shell mobile: protótipo `compras/manual.html` (mesmos dados, validações e envio).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[{ title: 'Compras', link: '/purchases' }]}
                titlePageTarget="Nova compra"
                openSnackbar={snackbar.open}
                setOpenSnackbar={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            >
                <MobilePurchaseManual
                    suppliers={suppliers}
                    supplierId={supplierId}
                    onSupplier={setSupplierId}
                    dueDate={dueDate}
                    onDueDate={setDueDate}
                    reference={reference}
                    onReference={setReference}
                    items={items}
                    money={money}
                    toBRL={toBRL}
                    toCents={toCents}
                    catName={catName}
                    subName={subName}
                    stockOf={stockOf}
                    query={query}
                    onQueryChange={onQueryChange}
                    q={q}
                    matches={matches}
                    exact={exact}
                    mode={mode}
                    selected={selected}
                    qty={qty}
                    onQty={(v) => { setQty(v); setQtyError(false); }}
                    qtyError={qtyError}
                    cost={cost}
                    onCost={(v) => { setCost(v); setCostError(false); }}
                    costError={costError}
                    form={form}
                    onForm={(patch) => setForm((f) => ({ ...f, ...patch }))}
                    showErrors={showErrors}
                    newErrors={newErrors}
                    categories={categories}
                    subcategories={subcategories}
                    selectExisting={selectExisting}
                    startNew={startNew}
                    resetEntry={resetEntry}
                    addFound={addFound}
                    addNew={addNew}
                    generateCode={generateCode}
                    setItemQty={setItemQty}
                    setItemCost={setItemCost}
                    removeItem={removeItem}
                    clearItems={clearItems}
                    totalCents={totalCents}
                    submitting={submitting}
                    onFinish={finish}
                    onCancel={() => navigate('/purchases')}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[{ title: 'Compras', link: '/purchases' }]}
            titlePageTarget="Nova compra"
            openSnackbar={snackbar.open}
            setOpenSnackbar={(open) => setSnackbar((s) => ({ ...s, open }))}
            message={snackbar.message}
            severity={snackbar.severity}
        >
            <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
                {/* ===== page-head ===== */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: '22px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '11px', flexShrink: 0, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <ShoppingCart size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Nova compra
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Lance os itens manualmente: produtos existentes atualizam o estoque; produtos novos podem ser cadastrados na hora.
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="outlined" startIcon={<FileUp size={16} strokeWidth={2} />} onClick={() => navigate('/purchases/import')}>
                        Importar XML/PDF
                    </Button>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 330px' }, gap: '18px', alignItems: 'start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 }}>
                        {/* ===== ADICIONAR ITEM ===== */}
                        <Box sx={cardSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '14px' }}>
                                <SectionIcon><Plus size={16} strokeWidth={2} /></SectionIcon>
                                <Box>
                                    <Typography component="h2" sx={sectionTitleSx}>Adicionar item</Typography>
                                    <Typography sx={sectionSubSx}>
                                        Digite o nome (ou código) do produto. <Kbd>Enter</Kbd> adiciona e volta o foco aqui.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    fullWidth
                                    label="Nome ou código do produto"
                                    value={query}
                                    inputRef={searchRef}
                                    onChange={onQueryChange}
                                    onKeyDown={onQueryKeyDown}
                                    onFocus={() => { setSearchFocused(true); if (q) setListOpen(true); }}
                                    onBlur={() => { setSearchFocused(false); setListOpen(false); }}
                                    InputLabelProps={{ shrink: searchFocused || Boolean(query) }}
                                    autoComplete="off"
                                    inputProps={{ role: 'combobox', 'aria-expanded': listOpen, 'aria-controls': 'purchase-manual-list' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ color: 'text.disabled', mr: 0 }}>
                                                <Search size={17} strokeWidth={2} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        ...standaloneFieldSx,
                                        '& .MuiOutlinedInput-root': { ...standaloneFieldSx['& .MuiOutlinedInput-root'], pl: '14px' },
                                        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': { padding: '21px 14px 7px 12px' },
                                        '& .MuiInputLabel-root': { ...standaloneFieldSx['& .MuiInputLabel-root'], transform: 'translate(42px, 17px) scale(1)' },
                                        '& .MuiInputLabel-root.MuiInputLabel-shrink': { ...standaloneFieldSx['& .MuiInputLabel-root.MuiInputLabel-shrink'], transform: 'translate(42px, 8px) scale(1)' },
                                    }}
                                />
                                {listOpen && q && (
                                    <Box
                                        id="purchase-manual-list"
                                        role="listbox"
                                        onMouseDown={(e) => e.preventDefault()}
                                        sx={{
                                            position: 'absolute', left: 0, right: 0, top: 'calc(100% + 6px)', zIndex: 20,
                                            backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider',
                                            borderRadius: '14px', boxShadow: (t) => t.customShadows.lg, p: '6px', maxHeight: 320, overflow: 'auto',
                                        }}
                                    >
                                        {matches.length > 0 ? (
                                            <>
                                                <Box sx={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', p: '8px 10px 4px' }}>
                                                    Produtos cadastrados
                                                </Box>
                                                {matches.map((p, i) => (
                                                    <Box
                                                        key={p.id}
                                                        role="option"
                                                        aria-selected={i === hl}
                                                        onClick={() => chooseOption(i)}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: '12px', p: '9px 10px', borderRadius: '10px', cursor: 'pointer',
                                                            backgroundColor: i === hl ? 'background.subtle' : 'transparent',
                                                            '&:hover': { backgroundColor: 'background.subtle' },
                                                        }}
                                                    >
                                                        <Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Package size={18} strokeWidth={1.8} />
                                                        </Box>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}><Mark text={p.name} query={q} /></Typography>
                                                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                                                Cód. {p.code}{catName(p) ? ` · ${catName(p)}` : ''}{subName(p) ? ` › ${subName(p)}` : ''}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ ml: 'auto', textAlign: 'right', fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                                            <Box component="b" sx={{ display: 'block', fontSize: 12.5, color: 'text.primary' }}>{p.quantity || 0} un</Box>
                                                            em estoque
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </>
                                        ) : (
                                            <Box sx={{ p: '12px 10px', fontSize: 12.5, color: 'text.secondary' }}>
                                                Nenhum produto encontrado para <b>“{q}”</b>.
                                            </Box>
                                        )}
                                        {!exact && (
                                            <>
                                                <Box sx={{ height: '1px', backgroundColor: 'divider', m: '6px 4px' }} />
                                                <Box
                                                    role="option"
                                                    aria-selected={hl === matches.length}
                                                    onClick={() => chooseOption(matches.length)}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: '12px', p: '9px 10px', borderRadius: '10px', cursor: 'pointer',
                                                        backgroundColor: hl === matches.length ? 'background.subtle' : 'transparent',
                                                        '&:hover': { backgroundColor: 'background.subtle' },
                                                    }}
                                                >
                                                    <Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, backgroundColor: 'secondary.bg', color: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Plus size={18} strokeWidth={2} />
                                                    </Box>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'secondary.main' }}>Cadastrar novo produto “{q}”</Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Defina código, categoria e subcategoria</Typography>
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* ----- vazio ----- */}
                            {mode === 'idle' && (
                                <Box sx={{ mt: 2, border: '1.5px dashed', borderColor: 'divider', borderRadius: '16px', p: '22px', display: 'flex', alignItems: 'center', gap: 2, color: 'text.secondary' }}>
                                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, backgroundColor: 'background.subtle', color: 'text.disabled', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Search size={22} strokeWidth={1.8} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary', mb: '2px' }}>Comece digitando o nome do produto</Typography>
                                        <Typography sx={{ fontSize: 12.5 }}>Se ele já existir, o estoque será atualizado. Se não existir, você poderá cadastrá-lo aqui mesmo.</Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* ----- encontrado ----- */}
                            {found && (
                                <Box sx={{ mt: 2, border: '1.5px solid', borderColor: 'primary.main', borderRadius: '16px', p: '16px 18px', backgroundColor: 'info.bg', display: { xs: 'flex', lg: 'block' }, flexWrap: 'wrap', alignItems: 'flex-start', columnGap: '12px', rowGap: '14px' }}>
                                    {/* Em telas menores o cabeçalho "dissolve" (contents) para o painel de estoque e os campos dividirem a mesma linha */}
                                    <Box sx={{ display: { xs: 'contents', lg: 'flex' }, alignItems: 'center', gap: '12px', mb: { lg: '14px' }, flexWrap: 'wrap' }}>
                                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', flexShrink: 0, backgroundColor: 'background.paper', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Package size={20} strokeWidth={1.8} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: { xs: '1 1 calc(100% - 70px)', lg: '0 1 auto' } }}>
                                            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 800, mb: '3px' }}>{selected.name}</Typography>
                                            <Box sx={{ fontSize: 12, color: 'text.secondary', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                <Pill tone="info">Existente</Pill>
                                                <span>Cód. <b>{selected.code}</b></span>
                                                {catName(selected) && (<><span>·</span><span>{catName(selected)}{subName(selected) ? ` › ${subName(selected)}` : ''}</span></>)}
                                            </Box>
                                        </Box>
                                        <Box aria-live="polite" sx={{ ml: { lg: 'auto' }, flexShrink: 0, display: 'flex', alignItems: 'center', gap: { xs: '6px', lg: '10px' }, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: { xs: '8px 10px', lg: '10px 14px' } }}>
                                            <Box sx={{ textAlign: 'center', minWidth: 62 }}>
                                                <Box component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled' }}>Estoque atual</Box>
                                                <Box component="b" sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.02em' }}>{curStock}</Box>
                                            </Box>
                                            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'success.main', backgroundColor: 'success.bg', p: '3px 8px', borderRadius: '20px' }}>+ {qtyNum}</Box>
                                            <Box sx={{ color: 'text.disabled', display: 'flex' }}><ArrowRight size={18} strokeWidth={2} /></Box>
                                            <Box sx={{ textAlign: 'center', minWidth: 62 }}>
                                                <Box component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled' }}>Após a compra</Box>
                                                <Box component="b" sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.02em', color: 'success.main' }}>{curStock + qtyNum}</Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: '8px', lg: '12px' }, flexWrap: 'wrap', flex: { xs: '1 1 320px', lg: 'none' } }}>
                                        <Box sx={{ width: { xs: 'auto', lg: 140 }, flex: { xs: '1 1 90px', lg: 'none' }, minWidth: 0 }}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Quantidade *"
                                                value={qty}
                                                inputRef={qtyRef}
                                                onChange={(e) => { setQty(e.target.value); setQtyError(false); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFound(); } }}
                                                inputProps={{ min: 1, step: 1 }}
                                                sx={{ ...standaloneFieldSx, ...errFieldSx(qtyError) }}
                                            />
                                            {qtyError && <FieldError>Informe uma quantidade maior que 0.</FieldError>}
                                        </Box>
                                        <Box sx={{ width: { xs: 'auto', lg: 180 }, flex: { xs: '1.4 1 110px', lg: 'none' }, minWidth: 0 }}>
                                            <TextField
                                                fullWidth
                                                label="Custo unitário (R$) *"
                                                value={cost}
                                                inputRef={costRef}
                                                onChange={(e) => { setCost(e.target.value); setCostError(false); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFound(); } }}
                                                inputProps={{ inputMode: 'decimal' }}
                                                sx={{ ...standaloneFieldSx, ...errFieldSx(costError) }}
                                            />
                                            {costError && <FieldError>Informe o custo (maior que 0).</FieldError>}
                                        </Box>
                                        <Button variant="contained" sx={{ height: 52, flexShrink: 0 }} startIcon={<Plus size={16} strokeWidth={2} />} onClick={addFound}>
                                            Adicionar
                                        </Button>
                                        <Box sx={{ ml: 'auto', alignSelf: 'center' }}>
                                            <Button variant="text" size="small" onClick={resetEntry}>Trocar produto</Button>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* ----- novo produto ----- */}
                            {mode === 'new' && (
                                <Box sx={{ mt: 2, border: '1.5px solid', borderColor: 'secondary.main', borderRadius: '16px', p: '16px 18px', backgroundColor: 'secondary.bg' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '14px', flexWrap: 'wrap' }}>
                                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', flexShrink: 0, backgroundColor: 'background.paper', color: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Plus size={20} strokeWidth={2} />
                                        </Box>
                                        <Box>
                                            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 800, mb: '3px' }}>Novo produto</Typography>
                                            <Box sx={{ fontSize: 12, color: 'text.secondary', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                <Pill tone="purple">Novo produto</Pill>
                                                <span>Não encontrado no cadastro — será criado ao finalizar a compra.</span>
                                            </Box>
                                        </Box>
                                        <Box sx={{ ml: 'auto' }}>
                                            <Button variant="text" size="small" onClick={resetEntry}>Cancelar cadastro</Button>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: '12px' }} onKeyDown={onNewKeyDown}>
                                        <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                                            <TextField
                                                fullWidth
                                                label="Nome do produto *"
                                                value={form.name}
                                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                                sx={{ ...standaloneFieldSx, ...errFieldSx(showErrors && newErrors.name) }}
                                            />
                                            {showErrors && newErrors.name && <FieldError>Informe o nome do produto.</FieldError>}
                                        </Box>
                                        <Box>
                                            <TextField
                                                fullWidth
                                                label="Código *"
                                                value={form.code}
                                                inputRef={newCodeRef}
                                                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                                                sx={{ ...standaloneFieldSx, ...errFieldSx(showErrors && newErrors.code) }}
                                            />
                                            {showErrors && newErrors.code && <FieldError>{newErrors.codeMsg}</FieldError>}
                                            <Box sx={{ mt: '5px' }}>
                                                <Button variant="text" size="small" sx={{ p: 0, minWidth: 0, fontSize: 12 }} onClick={generateCode}>Gerar automaticamente</Button>
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Dropdown
                                                label={reqLabel('Categoria')}
                                                labelVariant="static-micro"
                                                data={categories}
                                                selectedItem={form.category}
                                                handleChange={(value) => setForm((f) => ({ ...f, category: value, subcategory: '' }))}
                                                error={showErrors && newErrors.category}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                            {showErrors && newErrors.category && <FieldError>Selecione a categoria.</FieldError>}
                                        </Box>
                                        <Box>
                                            <Dropdown
                                                label={reqLabel('Subcategoria')}
                                                labelVariant="static-micro"
                                                data={subcategories}
                                                selectedItem={form.subcategory}
                                                disabled={!form.category}
                                                handleChange={(value) => setForm((f) => ({ ...f, subcategory: value }))}
                                                error={showErrors && newErrors.subcategory}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                            {showErrors && newErrors.subcategory && <FieldError>Selecione a subcategoria.</FieldError>}
                                        </Box>
                                        <Box>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Quantidade comprada *"
                                                value={form.qty}
                                                onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))}
                                                inputProps={{ min: 1, step: 1 }}
                                                sx={{ ...standaloneFieldSx, ...errFieldSx(showErrors && newErrors.qty) }}
                                            />
                                            {showErrors && newErrors.qty && <FieldError>Mínimo 1.</FieldError>}
                                        </Box>
                                        <Box>
                                            <TextField
                                                fullWidth
                                                label="Custo unitário (R$) *"
                                                value={form.cost}
                                                onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                                                inputProps={{ inputMode: 'decimal' }}
                                                sx={{ ...standaloneFieldSx, ...errFieldSx(showErrors && newErrors.cost) }}
                                            />
                                            {showErrors && newErrors.cost ? <FieldError>Informe o custo (maior que 0).</FieldError> : (
                                                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: '5px' }}>Forma o preço de venda com a margem da categoria.</Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mt: '14px', flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Kbd>Enter</Kbd> cadastra e adiciona à lista
                                        </Typography>
                                        <Button variant="contained" startIcon={<Plus size={16} strokeWidth={2} />} onClick={addNew}>
                                            Cadastrar e adicionar
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* ===== ITENS DA COMPRA ===== */}
                        <Box sx={cardSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: '14px', flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <SectionIcon><ListIcon size={16} strokeWidth={2} /></SectionIcon>
                                    <Box>
                                        <Typography component="h2" sx={sectionTitleSx}>
                                            Itens da compra <Box component="span" sx={{ ml: '6px' }}><Pill tone="grey">{items.length}</Pill></Box>
                                        </Typography>
                                        <Typography sx={sectionSubSx}>Ajuste a quantidade direto na linha ou remova itens.</Typography>
                                    </Box>
                                </Box>
                                <Button variant="outlined" size="small" color="inherit" disabled={!items.length || submitting} startIcon={<Trash2 size={14} strokeWidth={2} />} onClick={clearItems}>
                                    Limpar lista
                                </Button>
                            </Box>

                            <Box sx={{ overflowX: 'auto' }}>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <Box component="thead">
                                        <tr>
                                            {['Produto', 'Tipo', 'Categoria', 'Estoque', 'Quantidade', 'Custo unit.', 'Subtotal', ''].map((h) => (
                                                <Box component="th" key={h || 'actions'} sx={{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', fontWeight: 700, textAlign: 'left', p: '10px 12px', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' }}>{h}</Box>
                                            ))}
                                        </tr>
                                    </Box>
                                    <tbody>
                                        {!items.length && (
                                            <tr>
                                                <Box component="td" colSpan={8} sx={{ textAlign: 'center', p: '34px 16px', color: 'text.disabled', fontSize: 13 }}>
                                                    <Box sx={{ width: 46, height: 46, borderRadius: '50%', backgroundColor: 'background.subtle', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: '10px' }}>
                                                        <Package size={22} strokeWidth={1.8} />
                                                    </Box>
                                                    Nenhum item adicionado ainda.
                                                    <Box sx={{ fontSize: 12 }}>Os itens lançados acima aparecem aqui.</Box>
                                                </Box>
                                            </tr>
                                        )}
                                        {items.map((it) => {
                                            const isNew = it.kind === 'new';
                                            const base = isNew ? 0 : (it.p.quantity || 0);
                                            const cell = { p: '12px', borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle' };
                                            return (
                                                <Box
                                                    component="tr"
                                                    key={it.key}
                                                    sx={{
                                                        '&:hover': { backgroundColor: 'background.subtle' },
                                                        ...(it.key === flashing && { animation: 'fvFlash 1.4s ease', '@keyframes fvFlash': { '0%': { backgroundColor: 'success.bg' }, '100%': { backgroundColor: 'transparent' } } }),
                                                    }}
                                                >
                                                    <Box component="td" sx={cell}>
                                                        <Box sx={{ fontWeight: 700 }}>{it.p.name}</Box>
                                                        <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: '2px' }}>Cód. {it.p.code}</Box>
                                                        {it.error && (
                                                            <Box sx={{ fontSize: 11.5, color: 'error.main', fontWeight: 600, mt: '3px' }}>{it.error}</Box>
                                                        )}
                                                    </Box>
                                                    <Box component="td" sx={cell}>
                                                        {isNew
                                                            ? <Pill tone="purple" icon={<Plus size={12} strokeWidth={2.5} />}>Novo produto</Pill>
                                                            : <Pill tone="info">Existente</Pill>}
                                                    </Box>
                                                    <Box component="td" sx={cell}>
                                                        <div>{isNew ? it.p.category : (catName(it.p) || '—')}</div>
                                                        <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: '2px' }}>{isNew ? it.p.subcategory : subName(it.p)}</Box>
                                                    </Box>
                                                    <Box component="td" sx={cell}>
                                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                            {isNew ? <Box component="span" sx={{ color: 'text.disabled' }}>novo</Box> : base}
                                                            <ArrowRight size={12} strokeWidth={2.5} />
                                                            <Box component="b" sx={{ color: 'success.main' }}>{base + it.qty}</Box>
                                                        </Box>
                                                    </Box>
                                                    <Box component="td" sx={cell}>
                                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1.5px solid', borderColor: 'divider', borderRadius: '10px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
                                                            <IconButton aria-label="Diminuir" sx={qtyBtnSx} onClick={() => setItemQty(it.key, it.qty - 1)}><Minus size={14} strokeWidth={2.5} /></IconButton>
                                                            <Box
                                                                component="input"
                                                                type="number"
                                                                min={1}
                                                                aria-label="Quantidade"
                                                                value={it.qty}
                                                                onChange={(e) => setItemQty(it.key, parseInt(e.target.value, 10) || 1)}
                                                                sx={{
                                                                    width: 48, height: 32, border: 0, backgroundColor: 'transparent', textAlign: 'center', fontSize: 13, fontWeight: 700,
                                                                    color: 'text.primary', fontFamily: 'inherit', MozAppearance: 'textfield',
                                                                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                                                    '&:focus': { outline: 'none', backgroundColor: 'background.paper' },
                                                                }}
                                                            />
                                                            <IconButton aria-label="Aumentar" sx={qtyBtnSx} onClick={() => setItemQty(it.key, it.qty + 1)}><Plus size={14} strokeWidth={2.5} /></IconButton>
                                                        </Box>
                                                    </Box>
                                                    <Box component="td" sx={cell}>
                                                        <Box
                                                            component="input"
                                                            aria-label="Custo unitário"
                                                            inputMode="decimal"
                                                            value={it.cost || ''}
                                                            onChange={(e) => setItemCost(it.key, e.target.value)}
                                                            sx={{
                                                                width: 84, height: 32, px: '8px', textAlign: 'right', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                                                                color: 'text.primary', backgroundColor: 'background.subtle', borderRadius: '10px',
                                                                border: '1.5px solid', borderColor: toBRL(it.cost) ? 'divider' : 'error.main',
                                                                '&:focus': { outline: 'none', backgroundColor: 'background.paper' },
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box component="td" sx={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                        {money(toCents(it.cost) * it.qty)}
                                                    </Box>
                                                    <Box component="td" sx={{ ...cell, textAlign: 'right' }}>
                                                        <Tooltip title="Remover item">
                                                            <IconButton
                                                                aria-label="Remover item"
                                                                size="small"
                                                                onClick={() => removeItem(it.key)}
                                                                sx={{ width: 28, height: 28, borderRadius: '8px', border: '1px solid', borderColor: 'divider', color: 'text.secondary', '&:hover': { color: 'error.main', borderColor: 'error.main', backgroundColor: 'error.bg' } }}
                                                            >
                                                                <Trash2 size={14} strokeWidth={2} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </tbody>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* ===== RESUMO ===== */}
                    <Box sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
                        <Box sx={cardSx}>
                            <Typography component="h2" sx={{ ...sectionTitleSx, mb: '6px' }}>Resumo da compra</Typography>
                            <Box sx={{ display: 'grid', gap: '12px', mb: '6px', mt: '12px' }}>
                                <Dropdown
                                    label="Fornecedor (opcional)"
                                    labelVariant="static-micro"
                                    data={suppliers}
                                    selectedItem={supplierId}
                                    handleChange={setSupplierId}
                                    variant="outlined"
                                    size="medium"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    fullWidth
                                    label="Referência / nº da nota"
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    sx={standaloneFieldSx}
                                />
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                    <DatePicker
                                        label="Vencimento"
                                        format="DD/MM/YYYY"
                                        value={dueDate ? dayjs(dueDate) : null}
                                        onChange={(v) => setDueDate(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                sx: {
                                                    ...standaloneFieldSx,
                                                    '& .MuiPickersOutlinedInput-root': {
                                                        height: '52px',
                                                        borderRadius: '12px',
                                                        backgroundColor: 'background.subtle',
                                                    },
                                                    '& .MuiPickersInputBase-sectionsContainer': {
                                                        fontSize: '13.5px',
                                                        padding: '21px 0 7px 0',
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <Box sx={{ textAlign: 'center', pt: '14px', pb: '6px' }}>
                                <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled' }}>Unidades a entrar no estoque</Box>
                                <Box component="b" sx={{ display: 'block', fontSize: 34, fontWeight: 800, letterSpacing: '-.02em' }}>{totalUnits}</Box>
                            </Box>
                            <Box sx={{ height: '1px', backgroundColor: 'divider', my: '10px' }} />
                            {[
                                ['Itens na lista', <Box component="b" sx={{ color: 'text.primary' }}>{items.length}</Box>],
                                ['Produtos existentes', <Pill tone="info">{items.length - newCount}</Pill>],
                                ['Produtos novos a cadastrar', <Pill tone="purple">{newCount}</Pill>],
                                ['Total da compra', <Box component="b" sx={{ color: 'text.primary', fontSize: 15 }}>{money(totalCents)}</Box>],
                            ].map(([label, value]) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, py: 1, color: 'text.secondary' }}>
                                    <span>{label}</span>{value}
                                </Box>
                            ))}
                            <Box sx={{ fontSize: 11.5, color: 'text.disabled', display: 'flex', gap: 1, alignItems: 'flex-start', backgroundColor: 'background.subtle', borderRadius: '10px', p: '10px 12px', mt: '12px', lineHeight: 1.45 }}>
                                <Box sx={{ flexShrink: 0, mt: '1px', display: 'flex' }}><Info size={16} strokeWidth={2} /></Box>
                                <span>Ao finalizar, o estoque dos produtos existentes é incrementado e os produtos novos são cadastrados com a quantidade informada como estoque inicial.</span>
                            </Box>
                            <Box sx={{ display: 'grid', gap: '10px', mt: 2 }}>
                                <Button variant="contained" fullWidth disabled={!items.length || submitting} onClick={finish}>
                                    {submitting ? 'Lançando...' : 'Finalizar compra'}
                                </Button>
                                <Button variant="outlined" fullWidth disabled={submitting} onClick={() => navigate('/purchases')}>Cancelar</Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </PageScaffold>
    );
}
